import {
  CircularProgress,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useContext, useRef } from 'react';

import Snackbar from '../../components/Snackbar';
import { TitleBox } from '../../components/UI/Box';
import { GridMain } from '../../components/UI/Grid';
import { TitleTypography } from '../../components/UI/Typography';
import {
  IconInboxMS,
  IconKeyboardArrowLeftMS,
  IconKeyboardArrowRightMS,
} from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { CompanyCard } from './components/CompanyCard';
import { PreFlightDetail } from './components/PreFlightDetail';
import { RequestsList } from './components/RequestsList';
import { SourceTabs } from './components/SourceTabs';
import { Constants } from './constants';
import {
  CarouselWrapper,
  CompaniesScrollRow,
  CompaniesSection,
  DetailEmptyState,
  DetailPanel,
  ScrollIconButton,
  SectionDescription,
  SectionLabel,
} from './styles';
import { usePreFlight } from './usePreFlight';

const SCROLL_STEP = 240;

export function PreFlight(): JSX.Element {
  const { openSnackbar } = useContext(GlobalContext);
  const {
    companies,
    source,
    selectSource,
    loadingCompanies,
    preFlightRequests,
    loadingRequests,
    selectedCompanyId,
    setSelectedCompanyId,
    page,
    totalPages,
    loadNextPage,
    selectedPreflight,
    loadingDetail,
    handleGetPreflight,
    handleUpdateWorkOrder,
    loadingUpdateWorkOrder,
    handleApprove,
    handleReject,
    loadingApprove,
    loadingComplete,
    handleComplete,
    loadingReject,
    handleDeedUpload,
    handleDocumentAdded,
    handleDocumentDeleted,
  } = usePreFlight();

  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = (): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft -= SCROLL_STEP;
    }
  };

  const scrollRight = (): void => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += SCROLL_STEP;
    }
  };

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  return (
    <GridMain>
      <TitleBox>
        <TitleTypography>
          {IconInboxMS} {Constants.pageTitle}
        </TitleTypography>
      </TitleBox>
      <CompaniesSection>
        <SectionLabel>{Constants.companiesLabel}</SectionLabel>
        <SectionDescription>
          {Constants.companiesDescription}
        </SectionDescription>
        {loadingCompanies ? (
          <LinearProgress sx={{ margin: '40px' }} />
        ) : (
          <CarouselWrapper>
            {companies.length > 4 && (
              <ScrollIconButton color="primary" onClick={scrollLeft}>
                {IconKeyboardArrowLeftMS}
              </ScrollIconButton>
            )}
            <CompaniesScrollRow ref={scrollRef}>
              {companies.map((company) => (
                <CompanyCard
                  key={company.id}
                  id={company.id}
                  name={company.name}
                  pending={company.pending}
                  isSelected={selectedCompanyId === company.id}
                  onClick={() => setSelectedCompanyId(company.id)}
                />
              ))}
            </CompaniesScrollRow>
            {companies.length > 4 && (
              <ScrollIconButton color="primary" onClick={scrollRight}>
                {IconKeyboardArrowRightMS}
              </ScrollIconButton>
            )}
          </CarouselWrapper>
        )}
      </CompaniesSection>
      <SourceTabs value={source} onChange={selectSource} />
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <RequestsList
            selectedCompany={selectedCompany}
            requests={preFlightRequests}
            loading={loadingRequests}
            selectedCompanyId={selectedCompanyId}
            selectedPreflight={selectedPreflight}
            onSelectRequest={handleGetPreflight}
            page={page}
            totalPages={totalPages}
            onLoadMore={loadNextPage}
          />
        </Grid>
        <Grid item xs={9}>
          <DetailPanel>
            {selectedPreflight ? (
              <PreFlightDetail
                selectedPreflight={selectedPreflight}
                handleUpdateWorkOrder={handleUpdateWorkOrder}
                loadingUpdateWorkOrder={loadingUpdateWorkOrder}
                handleApprove={handleApprove}
                handleReject={handleReject}
                loadingApprove={loadingApprove}
                loadingComplete={loadingComplete}
                onComplete={(file) =>
                  handleComplete(selectedPreflight.id, file)
                }
                loadingReject={loadingReject}
                onDeedUpload={handleDeedUpload}
                onDocumentAdded={handleDocumentAdded}
                onDocumentDeleted={handleDocumentDeleted}
              />
            ) : (
              <DetailEmptyState>
                {loadingDetail ? (
                  <CircularProgress size={28} />
                ) : (
                  <Typography color="text.secondary" fontSize={14}>
                    {Constants.selectOsMessage}
                  </Typography>
                )}
              </DetailEmptyState>
            )}
          </DetailPanel>
        </Grid>
      </Grid>
      {openSnackbar && <Snackbar />}
    </GridMain>
  );
}
